/**
 * Command vrstva pro canvas mutace (Epic 6), navržená tak, aby ji Epic 13
 * (undo/redo) mohla později obalit zásobníkem beze změny tohoto souboru —
 * viz `doc/epic-13-undo-redo.md` kap. 5 ("ovlivňuje návrh Epic 6/7").
 *
 * Pravidla, která musí dodržet KAŽDÁ command factory v tomto souboru:
 *
 * 1. `apply`/`revert` jsou totální a nikdy nevyhazují — veškerá validace
 *    (max jedna skupina na tabulku, neprázdný název, ...) proběhne v UI
 *    handleru PŘED vytvořením commandu. Až Epic 13 začne commandy přehrávat
 *    ze zásobníku (undo/redo), nesmí narazit na chybu, kterou by musel řešit.
 * 2. Nová ID (nová skupina, nová poznámka) se generují JEDNOU při konstrukci
 *    commandu (`randomId()` níže), nikdy uvnitř `apply()` — jinak by redo
 *    v Epic 13 nebylo deterministické (dvojí `apply()` téhož commandu by
 *    pokaždé vyrobilo jiné ID).
 * 3. `revert()` data jsou snapshot dotčené části stavu pořízený PŘED
 *    mutací, ne inverzní výpočet — odpovídá doporučení v epic-13 kap. 8.
 * 4. Command samotný nikdy nevolá `setState` ani nemutuje svůj vstup —
 *    jediné místo, které smí měnit canvas stav, je `execute()` v `store.tsx`.
 */
import type { CanvasMetadata, ColorToken, EdgeRoute, Group, Note, NoteAttachment } from '@bits2schema/shared';
import { defaultEdgeRoute } from '@bits2schema/shared';
import type { Command as GenericCommand } from '../command/types';

export { randomId } from '../command/types';

export type CommandType =
  | 'moveTable'
  | 'moveGroup'
  | 'recolorTable'
  | 'createGroup'
  | 'renameGroup'
  | 'dissolveGroup'
  | 'addTableToGroup'
  | 'removeTableFromGroup'
  | 'createNote'
  | 'editNoteText'
  | 'moveNote'
  | 'deleteNote'
  | 'pinNote'
  | 'clearTableCanvasState'
  | 'autoLayout'
  | 'placeTable'
  | 'updateEdgeRoute'
  | 'resetEdgeRoute';

/** Konkrétní instance generického `Command<TState>` (`../command/types`) nad `CanvasMetadata`. */
export type Command = GenericCommand<CanvasMetadata, CommandType>;

type Point = { x: number; y: number };

/** Souvislé tažení tabulky myší = jeden krok (E13-US4), voláno na `onNodeDragStop`. */
export function createMoveTableCommand(tableId: string, from: Point, to: Point): Command {
  return {
    type: 'moveTable',
    descriptionKey: 'canvas.command.moveTable',
    apply: (canvas) => ({
      ...canvas,
      tablePositions: { ...canvas.tablePositions, [tableId]: to },
    }),
    revert: (canvas) => ({
      ...canvas,
      tablePositions: { ...canvas.tablePositions, [tableId]: from },
    }),
  };
}

/**
 * Přesun rámečku skupiny hýbe všemi členy o stejný vektor (rigidní pohyb, E6-US5).
 *
 * Bere explicitní `from`/`to` pozici KAŽDÉHO člena (dopočítanou volajícím z
 * `canvas.tablePositions`, s fallbackem na výchozí umístění — viz
 * `CanvasView.effectiveTablePositions`) — ne jen deltu aplikovanou na to, co
 * už v `tablePositions` je. Tabulka, která nebyla nikdy ručně přesunuta,
 * tam žádný záznam nemá; počítat s deltou by takovou tabulku v rámci
 * skupinového přesunu tiše přeskočilo.
 */
export function createMoveGroupCommand(moves: { tableId: string; from: Point; to: Point }[]): Command {
  const apply = (positions: CanvasMetadata['tablePositions'], key: 'from' | 'to') => {
    const next = { ...positions };
    for (const move of moves) next[move.tableId] = move[key];
    return next;
  };
  return {
    type: 'moveGroup',
    descriptionKey: 'canvas.command.moveGroup',
    apply: (canvas) => ({ ...canvas, tablePositions: apply(canvas.tablePositions, 'to') }),
    revert: (canvas) => ({ ...canvas, tablePositions: apply(canvas.tablePositions, 'from') }),
  };
}

export function createRecolorTableCommand(
  tableId: string,
  from: ColorToken | null,
  to: ColorToken | null,
): Command {
  const withColor = (colors: CanvasMetadata['tableColors'], color: ColorToken | null) => {
    const next = { ...colors };
    if (color) next[tableId] = color;
    else delete next[tableId];
    return next;
  };
  return {
    type: 'recolorTable',
    descriptionKey: 'canvas.command.recolorTable',
    apply: (canvas) => ({ ...canvas, tableColors: withColor(canvas.tableColors, to) }),
    revert: (canvas) => ({ ...canvas, tableColors: withColor(canvas.tableColors, from) }),
  };
}

export function createGroupCommand(group: Group): Command {
  return {
    type: 'createGroup',
    descriptionKey: 'canvas.command.createGroup',
    descriptionParams: { name: group.name },
    apply: (canvas) => ({ ...canvas, groups: [...canvas.groups, group] }),
    revert: (canvas) => ({
      ...canvas,
      groups: canvas.groups.filter((candidate) => candidate.id !== group.id),
    }),
  };
}

export function createRenameGroupCommand(groupId: string, from: string, to: string): Command {
  const rename = (groups: Group[], name: string) =>
    groups.map((group) => (group.id === groupId ? { ...group, name } : group));
  return {
    type: 'renameGroup',
    descriptionKey: 'canvas.command.renameGroup',
    descriptionParams: { name: to },
    apply: (canvas) => ({ ...canvas, groups: rename(canvas.groups, to) }),
    revert: (canvas) => ({ ...canvas, groups: rename(canvas.groups, from) }),
  };
}

/**
 * Zrušení skupiny odepne i poznámky připnuté k ní — jeden krok, undo obnoví
 * skupinu i původní připnutí (E13-US4 AC). `unpinnedNotes` nese pro každou
 * dotčenou poznámku volnou pozici dopočítanou volajícím PŘED voláním (z
 * bounding boxu skupiny v okamžiku zrušení) — `apply()` musí zůstat čisté.
 */
export function createDissolveGroupCommand(
  group: Group,
  unpinnedNotes: { noteId: string; position: Point }[],
): Command {
  const positionByNote = new Map(unpinnedNotes.map((entry) => [entry.noteId, entry.position]));
  return {
    type: 'dissolveGroup',
    descriptionKey: 'canvas.command.dissolveGroup',
    descriptionParams: { name: group.name },
    apply: (canvas) => ({
      ...canvas,
      groups: canvas.groups.filter((candidate) => candidate.id !== group.id),
      notes: canvas.notes.map((note) => {
        const position = positionByNote.get(note.id);
        if (!position) return note;
        return { ...note, attachment: { type: 'none' }, position };
      }),
    }),
    revert: (canvas) => ({
      ...canvas,
      groups: [...canvas.groups, group],
      notes: canvas.notes.map((note) => {
        if (!positionByNote.has(note.id)) return note;
        const { position: _drop, ...rest } = note;
        return { ...rest, attachment: { type: 'group', id: group.id } };
      }),
    }),
  };
}

export function createAddTableToGroupCommand(groupId: string, tableId: string): Command {
  const withMember = (groups: Group[], add: boolean) =>
    groups.map((group) => {
      if (group.id !== groupId) return group;
      return {
        ...group,
        memberTableIds: add
          ? [...group.memberTableIds, tableId]
          : group.memberTableIds.filter((id) => id !== tableId),
      };
    });
  return {
    type: 'addTableToGroup',
    descriptionKey: 'canvas.command.addTableToGroup',
    apply: (canvas) => ({ ...canvas, groups: withMember(canvas.groups, true) }),
    revert: (canvas) => ({ ...canvas, groups: withMember(canvas.groups, false) }),
  };
}

export function createRemoveTableFromGroupCommand(
  groupId: string,
  tableId: string,
  originalIndex: number,
): Command {
  const withoutMember = (groups: Group[]) =>
    groups.map((group) =>
      group.id === groupId
        ? { ...group, memberTableIds: group.memberTableIds.filter((id) => id !== tableId) }
        : group,
    );
  const withMemberReinserted = (groups: Group[]) =>
    groups.map((group) => {
      if (group.id !== groupId) return group;
      const memberTableIds = [...group.memberTableIds];
      memberTableIds.splice(originalIndex, 0, tableId);
      return { ...group, memberTableIds };
    });
  return {
    type: 'removeTableFromGroup',
    descriptionKey: 'canvas.command.removeTableFromGroup',
    apply: (canvas) => ({ ...canvas, groups: withoutMember(canvas.groups) }),
    revert: (canvas) => ({ ...canvas, groups: withMemberReinserted(canvas.groups) }),
  };
}

export function createNoteCommand(note: Note): Command {
  return {
    type: 'createNote',
    descriptionKey: 'canvas.command.createNote',
    apply: (canvas) => ({ ...canvas, notes: [...canvas.notes, note] }),
    revert: (canvas) => ({
      ...canvas,
      notes: canvas.notes.filter((candidate) => candidate.id !== note.id),
    }),
  };
}

export function createEditNoteTextCommand(noteId: string, from: string, to: string): Command {
  const withText = (notes: Note[], text: string) =>
    notes.map((note) => (note.id === noteId ? { ...note, text } : note));
  return {
    type: 'editNoteText',
    descriptionKey: 'canvas.command.editNoteText',
    apply: (canvas) => ({ ...canvas, notes: withText(canvas.notes, to) }),
    revert: (canvas) => ({ ...canvas, notes: withText(canvas.notes, from) }),
  };
}

/** Jen pro volné poznámky; gesto přesunu se coaluje stejně jako `moveTable`. */
export function createMoveNoteCommand(noteId: string, from: Point, to: Point): Command {
  const withPosition = (notes: Note[], position: Point) =>
    notes.map((note) => (note.id === noteId ? { ...note, position } : note));
  return {
    type: 'moveNote',
    descriptionKey: 'canvas.command.moveNote',
    apply: (canvas) => ({ ...canvas, notes: withPosition(canvas.notes, to) }),
    revert: (canvas) => ({ ...canvas, notes: withPosition(canvas.notes, from) }),
  };
}

export function createDeleteNoteCommand(note: Note, originalIndex: number): Command {
  return {
    type: 'deleteNote',
    descriptionKey: 'canvas.command.deleteNote',
    apply: (canvas) => ({
      ...canvas,
      notes: canvas.notes.filter((candidate) => candidate.id !== note.id),
    }),
    revert: (canvas) => {
      const notes = [...canvas.notes];
      notes.splice(originalIndex, 0, note);
      return { ...canvas, notes };
    },
  };
}

/**
 * Epic 4, E4-US3 — smazání tabulky ze schématu odebere i její canvas metadata
 * (pozice, barva). Malý cílený command místo jednoho obřího "smaž vše kolem
 * tabulky", protože poznámky a členství ve skupině už mají svoje existující
 * commandy (`createDeleteNoteCommand`, `createRemoveTableFromGroupCommand`) —
 * volající je zavolá zvlášť, sekvenčně, v jednom UI handleru (viz
 * `schema/dialogs/DeleteTableDialog.tsx`).
 */
export function createClearTableCanvasStateCommand(
  tableId: string,
  previousPosition: Point | undefined,
  previousColor: ColorToken | null,
): Command {
  const apply = (canvas: CanvasMetadata): CanvasMetadata => {
    const tablePositions = { ...canvas.tablePositions };
    delete tablePositions[tableId];
    const tableColors = { ...canvas.tableColors };
    delete tableColors[tableId];
    return { ...canvas, tablePositions, tableColors };
  };
  const revert = (canvas: CanvasMetadata): CanvasMetadata => {
    const tablePositions = { ...canvas.tablePositions };
    if (previousPosition) tablePositions[tableId] = previousPosition;
    const tableColors = { ...canvas.tableColors };
    if (previousColor) tableColors[tableId] = previousColor;
    return { ...canvas, tablePositions, tableColors };
  };
  return { type: 'clearTableCanvasState', descriptionKey: 'canvas.command.clearTableCanvasState', apply, revert };
}

interface AttachmentState {
  attachment: NoteAttachment;
  position?: Point;
}

/**
 * Pokrývá připnutí, odepnutí i přepnutí na jiný cíl jedním typem commandu —
 * volající dopočítá `to.position` (u odepnutí "na místě, kde byla
 * zobrazena") ještě před voláním, `apply()` samo žádnou pozici nepočítá.
 */
export function createPinNoteCommand(
  noteId: string,
  from: AttachmentState,
  to: AttachmentState,
): Command {
  const withState = (notes: Note[], state: AttachmentState) =>
    notes.map((note) => {
      if (note.id !== noteId) return note;
      const { position: _drop, ...rest } = note;
      return state.position ? { ...rest, attachment: state.attachment, position: state.position } : { ...rest, attachment: state.attachment };
    });
  return {
    type: 'pinNote',
    descriptionKey: 'canvas.command.pinNote',
    apply: (canvas) => ({ ...canvas, notes: withState(canvas.notes, to) }),
    revert: (canvas) => ({ ...canvas, notes: withState(canvas.notes, from) }),
  };
}

/**
 * Plný auto-layout (Epic 7, E7-US1/US2) — přepočte pozici KAŽDÉ tabulky
 * najednou (`nextPositions` je kompletní mapa, žádný merge) a resetuje
 * `edgeRoutes` na prázdnou mapu (= výchozí trasa pro každou vazbu,
 * `isManuallyRouted: false` u všech — kap. 2 rozhodnutí). `previousPositions`/
 * `previousEdgeRoutes` jsou snapshoty pořízené volajícím PŘED přepočtem
 * (epic-13 kap. 8 doporučení pro rozsáhlé změny — snapshot, ne inverzní výpočet).
 */
export function createAutoLayoutCommand(
  previousPositions: CanvasMetadata['tablePositions'],
  nextPositions: CanvasMetadata['tablePositions'],
  previousEdgeRoutes: CanvasMetadata['edgeRoutes'],
): Command {
  return {
    type: 'autoLayout',
    descriptionKey: 'canvas.command.autoLayout',
    apply: (canvas) => ({ ...canvas, tablePositions: nextPositions, edgeRoutes: {} }),
    revert: (canvas) => ({ ...canvas, tablePositions: previousPositions, edgeRoutes: previousEdgeRoutes }),
  };
}

/**
 * Automatické umístění jedné tabulky (E7-US3/US3b) — tvarem identické
 * `createMoveTableCommand`, ale vlastní `descriptionKey`, aby se v undo
 * historii odlišilo od ručního tažení tabulky myší.
 */
export function createPlaceTableCommand(tableId: string, from: Point | undefined, to: Point): Command {
  return {
    type: 'placeTable',
    descriptionKey: 'canvas.command.placeTable',
    apply: (canvas) => ({ ...canvas, tablePositions: { ...canvas.tablePositions, [tableId]: to } }),
    revert: (canvas) => {
      const tablePositions = { ...canvas.tablePositions };
      if (from) tablePositions[tableId] = from;
      else delete tablePositions[tableId];
      return { ...canvas, tablePositions };
    },
  };
}

/** Ruční úprava trasy vazby přidáním/přesunem/odebráním waypointů (E7-US4). */
export function createUpdateEdgeRouteCommand(foreignKeyId: string, from: EdgeRoute, to: EdgeRoute): Command {
  const withRoute = (edgeRoutes: CanvasMetadata['edgeRoutes'], route: EdgeRoute) => ({
    ...edgeRoutes,
    [foreignKeyId]: route,
  });
  return {
    type: 'updateEdgeRoute',
    descriptionKey: 'canvas.command.updateEdgeRoute',
    apply: (canvas) => ({ ...canvas, edgeRoutes: withRoute(canvas.edgeRoutes, to) }),
    revert: (canvas) => ({ ...canvas, edgeRoutes: withRoute(canvas.edgeRoutes, from) }),
  };
}

/** Reset ručně upravené trasy zpět na automaticky vypočtenou (E7-US5). */
export function createResetEdgeRouteCommand(foreignKeyId: string, previousRoute: EdgeRoute): Command {
  const withRoute = (edgeRoutes: CanvasMetadata['edgeRoutes'], route: EdgeRoute) => ({
    ...edgeRoutes,
    [foreignKeyId]: route,
  });
  return {
    type: 'resetEdgeRoute',
    descriptionKey: 'canvas.command.resetEdgeRoute',
    apply: (canvas) => ({ ...canvas, edgeRoutes: withRoute(canvas.edgeRoutes, defaultEdgeRoute()) }),
    revert: (canvas) => ({ ...canvas, edgeRoutes: withRoute(canvas.edgeRoutes, previousRoute) }),
  };
}
